/**
 * DOCS TABLE OF CONTENTS — local variant for the published docs-site.
 *
 * The design-system's DocsTableOfContents only supports two visual indent
 * levels (## flush, ### indented) and its extractHeadings regex drops `#`
 * lines entirely. Toggle blocks produce genuine H1/H2/H3 hierarchies that
 * need three distinct TOC tiers, so this local copy adds that third tier.
 *
 * Mirrors the structure of apps/web/components/docs/knowledge/docs-table-of-contents.tsx
 * with the addition of a third visual indent level.
 */

import * as React from "react";
import { cn } from "@siena-ai/design-system";

interface TocHeading {
  id: string;
  text: string;
  level: 1 | 2 | 3;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

/** Opens or closes a fenced code block. */
const FENCE_RE = /^\s*(?:```|~~~)/;

/**
 * A heading on this line, in either of the two forms a stored page can hold.
 *
 * The HTML pattern is deliberately unanchored: a toggle heading arrives as
 * `<summary><h1>Title</h1></summary>`, so the tag sits mid-line rather than
 * at its start.
 */
function matchHeading(line: string): { level: 1 | 2 | 3; text: string } | null {
  const mdMatch = line.match(/^(#{1,3})\s+(.+?)$/);
  if (mdMatch) {
    return {
      level: mdMatch[1].length as 1 | 2 | 3,
      text: mdMatch[2].replace(/\*\*/g, "").replace(/\*/g, "").trim(),
    };
  }

  const htmlMatch = line.match(/<h([123])[^>]*>(.+?)<\/h\1>/i);
  if (htmlMatch) {
    return {
      level: Number(htmlMatch[1]) as 1 | 2 | 3,
      text: htmlMatch[2].replace(/<[^>]+>/g, "").trim(),
    };
  }

  return null;
}

function extractHeadings(content: string): TocHeading[] {
  const headings: TocHeading[] = [];
  const seen = new Map<string, number>();
  const lines = content.split(/\r?\n/);
  let inFence = false;

  for (const line of lines) {
    if (FENCE_RE.test(line)) {
      inFence = !inFence;
    } else if (!inFence) {
      const found = matchHeading(line);
      if (found?.text) {
        let id = slugify(found.text);
        const count = seen.get(id) ?? 0;
        seen.set(id, count + 1);
        if (count > 0) id = `${id}-${count}`;
        headings.push({ id, text: found.text, level: found.level });
      }
    }
  }

  return headings;
}

export interface DocsTableOfContentsProps {
  /** Markdown content to extract headings from */
  content: string;
  /** Additional classes */
  className?: string;
  /** Top offset for sticky positioning (e.g. header height "3.5rem") */
  stickyOffset?: string;
}

export const DocsTableOfContents: React.FC<DocsTableOfContentsProps> = ({
  content,
  className,
  stickyOffset,
}) => {
  const headings = React.useMemo(() => extractHeadings(content), [content]);
  const [activeId, setActiveId] = React.useState<string>("");

  /**
   * Determine the minimum heading level so indentation is relative.
   * A page that only uses H2+H3 renders H2 flush and H3 indented —
   * identical to the old two-level behaviour.  A page with H1+H2+H3
   * (typical toggle-block pages) renders all three tiers.
   */
  const minLevel = React.useMemo(
    () =>
      headings.length > 0
        ? Math.min(...headings.map((h) => h.level))
        : 1,
    [headings],
  );

  React.useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0,
      },
    );

    for (const heading of headings) {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav className={cn("w-[200px] shrink-0", className)}>
      <div
        className="sticky"
        style={{
          top: stickyOffset
            ? `calc(${stickyOffset} + 1.5rem)`
            : "1.5rem",
        }}
      >
        <div className="text-xs font-medium text-muted-foreground mb-3">
          On this page
        </div>
        <ul className="space-y-1">
          {headings.map((heading, index) => {
            const depth = heading.level - minLevel;
            return (
              <li key={`${index}-${heading.id}`}>
                <a
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(heading.id);
                    if (el) {
                      el.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                      setActiveId(heading.id);
                    }
                  }}
                  className={cn(
                    "block text-xs leading-snug py-0.5 transition-colors",
                    depth === 1 && "pl-3",
                    depth >= 2 && "pl-6",
                    activeId === heading.id
                      ? "text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {heading.text}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export { slugify as slugifyHeading, extractHeadings };
